import React, { useLayoutEffect, useRef, useState } from 'react'
import MixedContentPreview, { MathpixScope } from '../UiElements/MixedContentPreview'
import './ExplainVisual.css'

// Draws a figure the server already computed (see helpers/explainVisuals.js
// for the shape guard). Nothing here evaluates math or turns figure text into
// markup: every string lands in a text node or in MixedContentPreview.

// The curve is drawn in a fixed square stretched over the figure. Mapping into
// it here keeps every coordinate small, so a graph far from the origin does
// not lose precision in the browser's single-precision SVG geometry.
const PLOT_UNITS = 1000
// Room around the plotted window, as a share of each span.
const PLOT_PADDING = 0.08
const X_TICK_TARGET = 6
const Y_TICK_TARGET = 5
const NICE_STEP_FACTORS = [1, 2, 5, 10]
// Within this share of an edge, a label moves to the far side of its anchor
// so the frame does not clip it.
const EDGE_SHARE = 0.12
// MixedContentPreview reads the hyphen in a bare "-4" as an operator and sets
// it as math, an unlabeled image to a screen reader, beside a plain-text
// "1.8". A true minus sign keeps every number in a column readable text.
const NEGATIVE_NUMBER = /^-(\d+(?:\.\d+)?)$/
const MINUS_SIGN = '\u2212'

// Diagram, histogram, and box plot labels are spaced and kept inside the
// figure's measured width at the figure font (11px times the app's 1.1 font
// scale). Until it is measured, or where ResizeObserver is missing, a figure
// is taken to be as narrow as it gets: the paper on a 320px phone.
const NARROWEST_FIGURE_PX = 200
const LABEL_CHAR_PX = 7
const LABEL_GAP_PX = 6

// In thousandths of the figure: the least room for the counts on the left and
// the bin edges underneath. Wider labels take more.
const HISTOGRAM_FRAME = { left: 110, right: 950, top: 70, bottom: 830 }

// In thousandths of the figure height. The five-number labels take the first
// row above the box that has room.
const BOX_PLOT_ROWS = {
  labels: [300, 170],
  boxTop: 390,
  capTop: 450,
  whisker: 520,
  capBottom: 590,
  boxBottom: 650,
  axis: 830
}

// Padding as a share of the figure's longer side, and the widest shape a
// diagram is drawn in (its short side grows to fit, so lengths stay true).
const GEOMETRY_PADDING = 0.1
const GEOMETRY_MAX_ASPECT = 2.5
// Angle marks, in plot units, capped to a share of the shorter arm.
const ANGLE_MARK_UNITS = 60
const ANGLE_MARK_ARM_SHARE = 0.35
const RIGHT_ANGLE_MARK_SHARE = 0.75
// The server's right-angle tolerance: within 1 degree of perpendicular.
const RIGHT_ANGLE_MAX_ABS_COSINE = Math.sin(Math.PI / 180)
// A point this far (in plot units) from the centre of the points has a side.
const LABEL_SIDE_UNITS = 20

const round2 = (value) => Math.round(value * 100) / 100

const percent = (units, total = PLOT_UNITS) => `${round2((units / total) * 100)}%`

// Multiples of 1, 2, or 5 times a power of ten inside [min, max], never
// closer together than `minStep`. None for a window too narrow for its
// distance from zero (data near 1e16 spanning 2): past 2^53, adding 1 to a
// multiple no longer changes it, and the loop would never end.
const niceTicks = (min, max, target, minStep = 0) => {
  const rough = (max - min) / target
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)))
  const step = Math.max(minStep, NICE_STEP_FACTORS
    .map((factor) => factor * magnitude)
    .find((candidate) => candidate >= rough * (1 - 1e-9)))
  const ticks = []
  const first = Math.ceil(min / step - 1e-9)
  const last = Math.floor(max / step + 1e-9)
  if (!Number.isSafeInteger(first) || !Number.isSafeInteger(last)) return ticks
  for (let multiple = first; multiple <= last; multiple += 1) {
    ticks.push(Number((multiple * step).toPrecision(12)))
  }
  return ticks
}

const tickText = (value) => String(Number(value.toPrecision(6))).replace('-', MINUS_SIGN)

const cellContent = (text) => text.replace(NEGATIVE_NUMBER, `${MINUS_SIGN}$1`)

// A highlight label sits directly over its own point (under it near the top),
// growing away from the nearer side so it neither leaves the frame nor
// drifts toward a neighboring point.
const labelAnchor = (column) => {
  if (column < PLOT_UNITS / 3) return 'start'
  if (column > (PLOT_UNITS * 2) / 3) return 'end'
  return 'middle'
}

const textWidth = (text) => text.length * LABEL_CHAR_PX

// Figure units to and from pixels, across a figure `width` pixels and
// `total` units wide.
const pixelsAt = (units, width, total = PLOT_UNITS) => (units / total) * width
const unitsOf = (pixels, width, total = PLOT_UNITS) => (pixels / width) * total

// Every how-many labels fit side by side when they are `spacingUnits` apart.
const labelStride = (texts, spacingUnits, width) => {
  const widest = Math.max(...texts.map(textWidth)) + LABEL_GAP_PX
  return Math.max(1, Math.ceil(widest / pixelsAt(spacingUnits, width)))
}

// Centred on its column unless that would cross the figure's edge.
const fittedAnchor = (text, column, width) => {
  const half = textWidth(text) / 2
  const at = pixelsAt(column, width)
  if (at < half) return 'start'
  if (width - at < half) return 'end'
  return 'middle'
}

// How far, in pixels, a label spanning [start, end] must move to sit inside
// a figure `width` wide. One wider than the figure is centred on it.
const shiftInside = (start, end, width) => {
  if (end - start >= width) return (width - start - end) / 2
  if (start < 0) return -start
  if (end > width) return width - end
  return 0
}

// A figure's drawn width in pixels. A hidden figure (width 0) keeps the last
// width it was drawn at.
const useFigureWidth = () => {
  const ref = useRef(null)
  const [width, setWidth] = useState(NARROWEST_FIGURE_PX)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node || typeof window.ResizeObserver !== 'function') return undefined
    const observer = new window.ResizeObserver(([entry]) => {
      const measured = Math.round(entry.contentRect.width)
      if (measured > 0) setWidth(measured)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return [ref, width]
}

// Every figure is one image to assistive tech, named by its alt, and never a
// tab stop.
const FigureSvg = ({ alt, compact, variant = '', style, figureRef, children }) => (
  <svg
    ref={figureRef}
    className={`explain-visual${variant && ` ${variant}`}${compact ? ' compact' : ''}`}
    role="img"
    aria-label={alt}
    focusable="false"
    style={style}
  >
    {children}
  </svg>
)

// Polyline point lists, split wherever the server left a gap (y === null).
// A lone sample is doubled so its round cap still draws a dot.
const curveRuns = (samples, toX, toY) => {
  const runs = []
  let run = []
  for (const { x, y } of samples) {
    if (y === null) {
      if (run.length) runs.push(run)
      run = []
    } else {
      run.push(`${toX(x)},${toY(y)}`)
    }
  }
  if (run.length) runs.push(run)
  return runs.map((points) => (points.length === 1 ? [points[0], points[0]] : points).join(' '))
}

const ExplainGraph = ({ visual, compact }) => {
  const { alt, variable, samples, highlights, asymptotes, hole } = visual
  const [xMin, xMax] = visual.domain
  const [yMin, yMax] = visual.yDomain
  const left = xMin - (xMax - xMin) * PLOT_PADDING
  const right = xMax + (xMax - xMin) * PLOT_PADDING
  const bottom = yMin - (yMax - yMin) * PLOT_PADDING
  const top = yMax + (yMax - yMin) * PLOT_PADDING
  const toX = (x) => round2(((x - left) / (right - left)) * PLOT_UNITS)
  const toY = (y) => round2(((top - y) / (top - bottom)) * PLOT_UNITS)

  // An axis is drawn where 0 falls inside the window.
  const xAxis = bottom <= 0 && top >= 0 ? toY(0) : null
  const yAxis = left <= 0 && right >= 0 ? toX(0) : null
  const xTicks = niceTicks(left, right, X_TICK_TARGET)
  const yTicks = niceTicks(bottom, top, Y_TICK_TARGET)
  // Labels only inside the requested window; 0 is left to the crossing axis.
  const xLabels = xTicks.filter((tick) => tick >= xMin && tick <= xMax && !(tick === 0 && yAxis !== null))
  const yLabels = yTicks.filter((tick) => tick >= yMin && tick <= yMax && !(tick === 0 && xAxis !== null))
  const xLabelRow = xAxis === null ? PLOT_UNITS : xAxis
  const xLabelsBelow = xLabelRow < PLOT_UNITS * (1 - EDGE_SHARE)
  const yLabelColumn = yAxis === null ? 0 : yAxis
  const yLabelsLeft = yLabelColumn > PLOT_UNITS * EDGE_SHARE

  return (
    <FigureSvg alt={alt} compact={compact}>
      <g className="explain-visual-grid">
        {xTicks.map((tick) => (
          <line key={`x${tick}`} x1={percent(toX(tick))} x2={percent(toX(tick))} y1="0" y2="100%" />
        ))}
        {yTicks.map((tick) => (
          <line key={`y${tick}`} x1="0" x2="100%" y1={percent(toY(tick))} y2={percent(toY(tick))} />
        ))}
      </g>
      {xAxis !== null && (
        <line className="explain-visual-axis" x1="0" x2="100%" y1={percent(xAxis)} y2={percent(xAxis)} />
      )}
      {yAxis !== null && (
        <line className="explain-visual-axis" x1={percent(yAxis)} x2={percent(yAxis)} y1="0" y2="100%" />
      )}
      {asymptotes.map(({ axis, at }) => (axis === 'vertical' ? (
        <line
          key={`v${at}`}
          className="explain-visual-asymptote"
          x1={percent(toX(at))}
          x2={percent(toX(at))}
          y1="0"
          y2="100%"
        />
      ) : (
        <line
          key={`h${at}`}
          className="explain-visual-asymptote"
          x1="0"
          x2="100%"
          y1={percent(toY(at))}
          y2={percent(toY(at))}
        />
      )))}

      <svg
        className="explain-visual-plot"
        width="100%"
        height="100%"
        viewBox={`0 0 ${PLOT_UNITS} ${PLOT_UNITS}`}
        preserveAspectRatio="none"
      >
        {curveRuns(samples, toX, toY).map((points, index) => (
          <polyline
            key={index}
            className="explain-visual-curve"
            points={points}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {hole && (
        <circle className="explain-visual-hole" cx={percent(toX(hole.x))} cy={percent(toY(hole.y))} r="5" />
      )}
      {highlights.map(({ x, y }, index) => (
        <circle key={index} className="explain-visual-point" cx={percent(toX(x))} cy={percent(toY(y))} r="4" />
      ))}

      {xLabels.map((tick) => (
        <text
          key={`x${tick}`}
          x={percent(toX(tick))}
          y={percent(xLabelRow)}
          dy={xLabelsBelow ? '1.25em' : '-0.5em'}
          textAnchor="middle"
        >
          {tickText(tick)}
        </text>
      ))}
      {yLabels.map((tick) => (
        <text
          key={`y${tick}`}
          x={percent(yLabelColumn)}
          y={percent(toY(tick))}
          dx={yLabelsLeft ? '-0.45em' : '0.45em'}
          dy="0.35em"
          textAnchor={yLabelsLeft ? 'end' : 'start'}
        >
          {tickText(tick)}
        </text>
      ))}
      {xAxis !== null && (
        <text className="explain-visual-axis-name" x="100%" y={percent(xAxis)} dx="-0.5em" dy="-0.5em" textAnchor="end">
          {variable === 'theta' ? 'θ' : variable}
        </text>
      )}
      {yAxis !== null && (
        <text className="explain-visual-axis-name" x={percent(yAxis)} y="0" dx="0.5em" dy="1.1em" textAnchor="start">
          {variable === 'y' ? 'x' : 'y'}
        </text>
      )}
      {highlights.map(({ x, y, label }, index) => {
        const column = toX(x)
        const row = toY(y)
        return (
          <text
            key={index}
            className="explain-visual-label"
            x={percent(column)}
            y={percent(row)}
            dy={row < PLOT_UNITS * EDGE_SHARE ? '1.45em' : '-0.75em'}
            textAnchor={labelAnchor(column)}
          >
            {label}
          </text>
        )
      })}
    </FigureSvg>
  )
}

// The figure's box, padded, with its short side grown about the middle when
// the box is too long to draw. One scale serves both axes, so a 3-4-5
// triangle keeps its shape; y is flipped to read upward.
const geometryFrame = ([xMin, yMin, xMax, yMax]) => {
  const pad = Math.max(xMax - xMin, yMax - yMin) * GEOMETRY_PADDING
  const width = Math.max(xMax - xMin + 2 * pad, (yMax - yMin + 2 * pad) / GEOMETRY_MAX_ASPECT)
  const height = Math.max(yMax - yMin + 2 * pad, width / GEOMETRY_MAX_ASPECT)
  const scale = PLOT_UNITS / Math.max(width, height)
  const left = (xMin + xMax - width) / 2
  const top = (yMin + yMax + height) / 2
  return {
    width: round2(width * scale),
    height: round2(height * scale),
    aspect: Number((width / height).toFixed(4)),
    scale,
    map: ([x, y]) => [round2((x - left) * scale), round2((top - y) * scale)]
  }
}

const distance = ([x1, y1], [x2, y2]) => Math.hypot(x2 - x1, y2 - y1)

const pointList = (points) => points.map(([x, y]) => `${x},${y}`).join(' ')

// An arc between the two arms, or the square corner mark when they are
// perpendicular. Worked in screen orientation (y down), so the sweep flag
// follows the turn from the first arm to the second.
const angleMark = (key, first, vertex, last) => {
  const arm = (end) => {
    const length = distance(vertex, end)
    return [(end[0] - vertex[0]) / length, (end[1] - vertex[1]) / length, length]
  }
  const [ux, uy, uLength] = arm(first)
  const [vx, vy, vLength] = arm(last)
  if (!uLength || !vLength) return null
  const reach = Math.min(ANGLE_MARK_UNITS, ANGLE_MARK_ARM_SHARE * Math.min(uLength, vLength))
  const along = (dx, dy, length) => [round2(vertex[0] + dx * length), round2(vertex[1] + dy * length)]

  if (Math.abs(ux * vx + uy * vy) <= RIGHT_ANGLE_MAX_ABS_COSINE) {
    const corner = reach * RIGHT_ANGLE_MARK_SHARE
    return (
      <polyline
        key={key}
        className="explain-visual-angle"
        points={pointList([along(ux, uy, corner), along(ux + vx, uy + vy, corner), along(vx, vy, corner)])}
        vectorEffect="non-scaling-stroke"
      />
    )
  }
  const sweep = ux * vy - uy * vx > 0 ? 1 : 0
  const [startX, startY] = along(ux, uy, reach)
  const [endX, endY] = along(vx, vy, reach)
  return (
    <path
      key={key}
      className="explain-visual-angle"
      d={`M ${startX} ${startY} A ${round2(reach)} ${round2(reach)} 0 0 ${sweep} ${endX} ${endY}`}
      vectorEffect="non-scaling-stroke"
    />
  )
}

// The strokes and fills, drawn in the figure's own units. Points and text are
// drawn over them at screen size; any other type draws nothing. An id with no
// point behind it skips its element rather than throwing.
const geometryShape = (element, key, at, frame) => {
  switch (element.type) {
    case 'segment':
    case 'line': {
      const from = at(element.from)
      const to = at(element.to)
      const length = from && to ? distance(from, to) : 0
      if (!length) return null
      // A line runs edge to edge: both points are inside the frame, so going
      // the frame's width plus height past each one leaves it.
      const reach = element.type === 'line' ? (frame.width + frame.height) / length : 0
      const dx = (to[0] - from[0]) * reach
      const dy = (to[1] - from[1]) * reach
      return (
        <line
          key={key}
          className="explain-visual-shape"
          x1={round2(from[0] - dx)}
          y1={round2(from[1] - dy)}
          x2={round2(to[0] + dx)}
          y2={round2(to[1] + dy)}
          vectorEffect="non-scaling-stroke"
        />
      )
    }
    case 'polygon': {
      const corners = element.vertices.map(at)
      if (corners.includes(undefined)) return null
      return (
        <polygon
          key={key}
          className="explain-visual-polygon"
          points={pointList(corners)}
          vectorEffect="non-scaling-stroke"
        />
      )
    }
    case 'circle': {
      const center = at(element.center)
      if (!center) return null
      const radius = element.through === undefined
        ? element.radius * frame.scale
        : distance(center, at(element.through) || center)
      if (!(radius > 0)) return null
      return (
        <circle
          key={key}
          className="explain-visual-shape"
          cx={center[0]}
          cy={center[1]}
          r={round2(radius)}
          vectorEffect="non-scaling-stroke"
        />
      )
    }
    case 'angle': {
      const [first, vertex, last] = element.points.map(at)
      return first && vertex && last ? angleMark(key, first, vertex, last) : null
    }
    default:
      return null
  }
}

// -1, 0, or 1: which side of the centre an offset falls on.
const sideOf = (offset) => (Math.abs(offset) < LABEL_SIDE_UNITS ? 0 : Math.sign(offset))

const ExplainGeometry = ({ visual, compact }) => {
  const { alt, elements } = visual
  const [figureRef, width] = useFigureWidth()
  const frame = geometryFrame(visual.boundingBox)
  const points = new Map(elements
    .filter((element) => element.type === 'point')
    .map((element) => [element.id, frame.map(element.coords)]))
  const at = (id) => points.get(id)
  const placed = [...points.values()]
  const centreX = placed.reduce((sum, [x]) => sum + x, 0) / placed.length
  const centreY = placed.reduce((sum, [, y]) => sum + y, 0) / placed.length
  const x = (units) => percent(units, frame.width)
  const y = (units) => percent(units, frame.height)
  // A label's column, moved just far enough that its text, which starts
  // `lead` pixels from the column, stays inside the figure.
  const keptInside = (column, lead, text) => {
    const start = pixelsAt(column, width, frame.width) + lead
    return column + unitsOf(shiftInside(start, start + textWidth(text), width), width, frame.width)
  }

  // A point's label sits on its outer side, away from the centre of all the
  // points, so it stays off the figure's own edges.
  // A lone point, or one at the centre, takes its label up and to the right.
  const pointLabel = (key, { id, label }) => {
    const [column, row] = at(id)
    const centred = !sideOf(column - centreX) && !sideOf(row - centreY)
    const across = centred ? 1 : sideOf(column - centreX)
    const down = centred ? -1 : sideOf(row - centreY)
    const lead = across < 0 ? -LABEL_GAP_PX - textWidth(label) : across > 0 ? LABEL_GAP_PX : -textWidth(label) / 2
    return (
      <text
        key={key}
        className="explain-visual-label"
        x={x(keptInside(column, lead, label))}
        y={y(row)}
        dx={`${across * 0.45}em`}
        dy={down < 0 ? '-0.5em' : down > 0 ? '1.2em' : '0.35em'}
        textAnchor={across < 0 ? 'end' : across > 0 ? 'start' : 'middle'}
      >
        {label}
      </text>
    )
  }

  return (
    <FigureSvg
      alt={alt}
      compact={compact}
      variant="geometry"
      style={{ '--explain-visual-aspect': frame.aspect }}
      figureRef={figureRef}
    >
      <svg
        className="explain-visual-plot"
        width="100%"
        height="100%"
        viewBox={`0 0 ${frame.width} ${frame.height}`}
        preserveAspectRatio="none"
      >
        {elements.map((element, index) => geometryShape(element, index, at, frame))}
      </svg>
      {elements.map((element, index) => {
        if (element.type !== 'point') return null
        const [column, row] = at(element.id)
        return <circle key={index} className="explain-visual-point" cx={x(column)} cy={y(row)} r="3.5" />
      })}
      {elements.map((element, index) => {
        if (element.type === 'point') return element.label ? pointLabel(index, element) : null
        if (element.type !== 'text') return null
        const [column, row] = frame.map(element.at)
        const inside = keptInside(column, -textWidth(element.content) / 2, element.content)
        return (
          <text key={index} x={x(inside)} y={y(row)} dy="0.35em" textAnchor="middle">
            {element.content}
          </text>
        )
      })}
    </FigureSvg>
  )
}

// Bars from the server's own bins: the browser never counts the data.
const ExplainHistogram = ({ visual, compact }) => {
  const { alt, bins } = visual
  const [figureRef, width] = useFigureWidth()
  const { top, bottom } = HISTOGRAM_FRAME
  const first = bins[0].start
  const last = bins[bins.length - 1].end
  const maxCount = Math.max(...bins.map((bin) => bin.count))
  // The count axis ends on the first gridline at or above the tallest bar, so
  // its labels stay evenly spaced. A count is at least 1, so there are always
  // two ticks and the second is the step.
  const ticks = niceTicks(0, maxCount, Y_TICK_TARGET, 1)
  const counts = ticks[ticks.length - 1] < maxCount ? [...ticks, ticks[ticks.length - 1] + ticks[1]] : ticks
  const scaleTop = counts[counts.length - 1]
  const edges = [first, ...bins.map((bin) => bin.end)]
  const edgeTexts = edges.map(tickText)
  // The frame gives way to the widest count beside it and to the outer edge
  // labels, which are centred on its corners.
  const halfLabel = (text) => unitsOf((textWidth(text) + LABEL_GAP_PX) / 2, width)
  const left = Math.max(
    HISTOGRAM_FRAME.left,
    unitsOf(textWidth(String(scaleTop)) + LABEL_GAP_PX, width),
    halfLabel(edgeTexts[0])
  )
  const right = Math.min(HISTOGRAM_FRAME.right, PLOT_UNITS - halfLabel(edgeTexts[edges.length - 1]))
  const toX = (value) => left + ((value - first) / (last - first)) * (right - left)
  const toY = (count) => bottom - (count / scaleTop) * (bottom - top)
  const stride = labelStride(edgeTexts, (right - left) / bins.length, width)

  return (
    <FigureSvg alt={alt} compact={compact} variant="histogram" figureRef={figureRef}>
      <g className="explain-visual-grid">
        {counts.filter((count) => count > 0).map((count) => (
          <line key={count} x1={percent(left)} x2={percent(right)} y1={percent(toY(count))} y2={percent(toY(count))} />
        ))}
      </g>
      {bins.map((bin, index) => (
        <rect
          key={index}
          className="explain-visual-bar"
          x={percent(toX(bin.start))}
          y={percent(toY(bin.count))}
          width={percent(toX(bin.end) - toX(bin.start))}
          height={percent(bottom - toY(bin.count))}
        />
      ))}
      <line className="explain-visual-axis" x1={percent(left)} x2={percent(right)} y1={percent(bottom)} y2={percent(bottom)} />
      <line className="explain-visual-axis" x1={percent(left)} x2={percent(left)} y1={percent(top)} y2={percent(bottom)} />
      {counts.map((count) => (
        <text key={`c${count}`} x={percent(left)} y={percent(toY(count))} dx="-0.45em" dy="0.35em" textAnchor="end">
          {count}
        </text>
      ))}
      {edges.map((edge, index) => (index % stride ? null : (
        <text key={`e${index}`} x={percent(toX(edge))} y={percent(bottom)} dy="1.25em" textAnchor="middle">
          {edgeTexts[index]}
        </text>
      )))}
    </FigureSvg>
  )
}

// One label per distinct value, each over its own line, in the first row
// that leaves a gap after the label before it. With no row clear, it slides
// right of the earliest-ending one (staying inside the figure) rather than
// printing over it.
const fiveNumberLabels = (values, toX, width) => {
  const rowEnds = BOX_PLOT_ROWS.labels.map(() => -Infinity)
  const labels = []
  for (const value of values) {
    const text = tickText(value)
    if (labels.some((label) => label.text === text)) continue
    const column = toX(value)
    const anchor = fittedAnchor(text, column, width)
    const span = textWidth(text)
    const centre = pixelsAt(column, width)
    const start = anchor === 'start' ? centre : anchor === 'end' ? centre - span : centre - span / 2
    const free = rowEnds.findIndex((end) => start >= end + LABEL_GAP_PX)
    if (free >= 0) {
      rowEnds[free] = start + span
      labels.push({ text, column, anchor, row: free })
    } else {
      const row = rowEnds.indexOf(Math.min(...rowEnds))
      const slid = Math.min(rowEnds[row] + LABEL_GAP_PX, width - span)
      rowEnds[row] = slid + span
      labels.push({ text, column: unitsOf(slid, width), anchor: 'start', row })
    }
  }
  return labels
}

// Drawn from the server's five-number summary: the browser never sorts the
// data or computes a quartile.
const ExplainBoxPlot = ({ visual, compact }) => {
  const { alt, fiveNumber } = visual
  const [figureRef, width] = useFigureWidth()
  const { min, q1, median, q3, max } = fiveNumber
  // A data set of one repeated value still needs a scale to sit on.
  const half = max > min ? 0 : Math.max(1, Math.abs(min) * 0.1)
  const low = min - half
  const high = max + half
  const left = low - (high - low) * PLOT_PADDING
  const right = high + (high - low) * PLOT_PADDING
  const toX = (value) => round2(((value - left) / (right - left)) * PLOT_UNITS)
  const ticks = niceTicks(left, right, X_TICK_TARGET)
  // Axis numbers inside the data's span, thinned to fit side by side; one
  // that would cross the figure's edge is left to its gridline.
  const tickStride = ticks.length > 1
    ? labelStride(ticks.map(tickText), toX(ticks[1]) - toX(ticks[0]), width)
    : 1
  const tickLabels = ticks
    .filter((tick) => tick >= low && tick <= high)
    .filter((tick, index) => index % tickStride === 0 && fittedAnchor(tickText(tick), toX(tick), width) === 'middle')
  const rows = BOX_PLOT_ROWS
  const whisker = percent(rows.whisker)

  return (
    <FigureSvg alt={alt} compact={compact} variant="box-plot" figureRef={figureRef}>
      <g className="explain-visual-grid">
        {ticks.map((tick) => (
          <line key={tick} x1={percent(toX(tick))} x2={percent(toX(tick))} y1="0" y2={percent(rows.axis)} />
        ))}
      </g>
      <line className="explain-visual-axis" x1="0" x2="100%" y1={percent(rows.axis)} y2={percent(rows.axis)} />
      <line className="explain-visual-whisker" x1={percent(toX(min))} x2={percent(toX(q1))} y1={whisker} y2={whisker} />
      <line className="explain-visual-whisker" x1={percent(toX(q3))} x2={percent(toX(max))} y1={whisker} y2={whisker} />
      {[min, max].map((value, index) => (
        <line
          key={index}
          className="explain-visual-whisker"
          x1={percent(toX(value))}
          x2={percent(toX(value))}
          y1={percent(rows.capTop)}
          y2={percent(rows.capBottom)}
        />
      ))}
      <rect
        className="explain-visual-box"
        x={percent(toX(q1))}
        y={percent(rows.boxTop)}
        width={percent(toX(q3) - toX(q1))}
        height={percent(rows.boxBottom - rows.boxTop)}
      />
      <line
        className="explain-visual-median"
        x1={percent(toX(median))}
        x2={percent(toX(median))}
        y1={percent(rows.boxTop)}
        y2={percent(rows.boxBottom)}
      />
      {tickLabels.map((tick) => (
        <text key={tick} x={percent(toX(tick))} y={percent(rows.axis)} dy="1.25em" textAnchor="middle">
          {tickText(tick)}
        </text>
      ))}
      {fiveNumberLabels([min, q1, median, q3, max], toX, width).map(({ text, column, anchor, row }) => (
        <text
          key={text}
          className="explain-visual-label"
          x={percent(column)}
          y={percent(rows.labels[row])}
          textAnchor={anchor}
        >
          {text}
        </text>
      ))}
    </FigureSvg>
  )
}

// One preview per cell, so the Mathpix stylesheet is installed once for the
// whole table instead of once per cell.
const ExplainTable = ({ caption, columns, rows }) => (
  <MathpixScope>
    <div className="explain-visual-table-wrap">
      <table className="explain-visual-table">
        <caption className="explain-visual-caption">{caption}</caption>
        <thead>
          <tr>
            {columns.map((header, index) => (
              <th key={index} scope="col">
                <MixedContentPreview content={cellContent(header)} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.cells.map((cell, cellIndex) => (
                <td key={cellIndex}>
                  <MixedContentPreview content={cellContent(cell)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </MathpixScope>
)

/**
 * A step's (or the final answer's) figure. A fixed switch on `kind`: a kind
 * this build does not draw renders nothing, and its step stays text.
 * `compact` is the worksheet layout's shorter graph.
 */
const ExplainVisual = ({ visual = null, compact = false }) => {
  switch (visual?.kind) {
    case 'function':
    case 'limit':
      return (
        <div className="explain-visual-figure">
          <ExplainGraph visual={visual} compact={compact} />
          {visual.table && (
            <ExplainTable
              caption={`Table of values for ${visual.expr}`}
              columns={visual.table.columns}
              rows={visual.table.rows}
            />
          )}
        </div>
      )
    case 'table':
      return (
        <div className="explain-visual-figure">
          <ExplainTable caption={visual.alt} columns={visual.columns} rows={visual.rows} />
        </div>
      )
    case 'geometry':
      return (
        <div className="explain-visual-figure">
          <ExplainGeometry visual={visual} compact={compact} />
        </div>
      )
    case 'histogram':
      return (
        <div className="explain-visual-figure">
          <ExplainHistogram visual={visual} compact={compact} />
        </div>
      )
    case 'boxPlot':
      return (
        <div className="explain-visual-figure">
          <ExplainBoxPlot visual={visual} compact={compact} />
        </div>
      )
    default:
      return null
  }
}

export default React.memo(ExplainVisual)
